import React, { useState, useEffect, useRef } from 'react'
import { useMsal, MsalProvider } from '@azure/msal-react'
import { PublicClientApplication } from '@azure/msal-browser'
import {
  getClientID,
  getBaseUrl,
  getAzureClientID,
  getAzureTenantID,
  LOGIN_ENDPOINT,
  REFRESH_TOKEN_URL,
} from '../../constants'
import { useStyles } from './QuizOnboardingPageCTAStyles'

const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
  prompt: 'select_account',
}

const postLogin = async (token, provider, tenantID) => {
  const res = await fetch(`${getBaseUrl()}${LOGIN_ENDPOINT}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      provider,
      providerData: { token },
      platform: 'WEB',
      tenantID,
    }),
  })
  const data = await res.json()
  if (!data?.data) throw new Error('Login failed')
  return data.data
}

const postRefresh = async (refreshToken, tenantID) => {
  const res = await fetch(`${getBaseUrl()}${REFRESH_TOKEN_URL}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken, tenantID }),
  })
  const data = await res.json()
  if (!data?.data) throw new Error('Refresh failed')
  return data.data
}

// Needs to be a separate component so useMsal() is called inside MsalProvider
const MicrosoftLoginButton = ({ onLoginFlow, setLoading, classes }) => {
  const { instance, inProgress } = useMsal()
  const [msalError, setMsalError] = useState(null)

  const handleMicrosoftLogin = async () => {
    if (inProgress !== 'none') return
    setMsalError(null)
    setLoading(true)
    try {
      const response = await instance.loginPopup(loginRequest)
      const token = response?.idToken
      if (!token) throw new Error('No ID token returned')
      await onLoginFlow(token, 'MICROSOFT')
    } catch (err) {
      if (
        err.message?.includes('user_cancelled') ||
        err.message?.includes('access_denied')
      ) {
        setLoading(false)
        return
      }
      if (err.message?.includes('interaction_in_progress')) {
        setMsalError(
          'Login state conflict. Please clear your browser cache and try again.'
        )
        setLoading(false)
        return
      }
      console.error('Microsoft login error:', err)
      setLoading(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={handleMicrosoftLogin}
        className={classes.microsoftButton}
      >
        <MicrosoftIcon />
        Sign in with Microsoft
      </button>
      {msalError && <p className={classes.msalError}>{msalError}</p>}
    </>
  )
}

const Login = ({ quizInfoDetails, handleResponse }) => {
  const [loading, setLoading] = useState(false)
  const [msalInstance, setMsalInstance] = useState(null)
  const googleInitialized = useRef(false)
  const classes = useStyles()
  const tenantID = quizInfoDetails?.orgID

  useEffect(() => {
    try {
      const clientId = getAzureClientID()
      const tenantId = getAzureTenantID()

      if (
        !clientId ||
        !tenantId ||
        clientId.trim() === '' ||
        tenantId.trim() === ''
      ) {
        return
      }

      setMsalInstance(
        new PublicClientApplication({
          auth: {
            clientId,
            authority: `https://login.microsoftonline.com/${tenantId}`,
            redirectUri: `${window.location.origin}/blank.html`,
            navigateToLoginRequestUrl: false,
          },
          cache: {
            cacheLocation: 'sessionStorage',
            storeAuthStateInCookie: false,
          },
        })
      )
    } catch (err) {
      console.error('MSAL init failed:', err)
      // msalInstance stays null, Microsoft button just won't render
    }
  }, [])

  const handleLoginFlow = async (token, provider) => {
    setLoading(true)
    try {
      const loginData = await postLogin(token, provider, tenantID)
      let { accessToken, refreshToken } = loginData

      if (!accessToken) {
        const refreshed = await postRefresh(loginData.refreshToken, tenantID)
        accessToken = refreshed.accessToken
        refreshToken = refreshed.refreshToken
      }

      handleResponse({ accessToken, refreshToken, tenantID })
    } catch (err) {
      console.error('Login error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (googleInitialized.current) return
    const init = () => {
      if (window.google?.accounts) {
        googleInitialized.current = true
        window.google.accounts.id.initialize({
          client_id: getClientID(),
          callback: (res) => {
            if (res?.credential) {
              handleLoginFlow(res.credential, 'GOOGLE')
            }
          },
        })
        window.google.accounts.id.renderButton(
          document.getElementById('quiz-google-signin'),
          {
            theme: 'light',
            size: 'large',
            type: 'standard',
            shape: 'rectangular',
            text: 'signin_with',
            logo_alignment: 'center',
            width: '240',
            auto_select: 'true',
          }
        )
      } else {
        setTimeout(init, 1500)
      }
    }
    init()
  }, [])

  if (loading) {
    return <div className={classes.signingIn}>Signing in...</div>
  }

  return (
    <div className={classes.loginButtonsWrapper}>
      <div id="quiz-google-signin" />
      {msalInstance && (
        <MsalProvider instance={msalInstance}>
          <MicrosoftLoginButton
            onLoginFlow={handleLoginFlow}
            setLoading={setLoading}
            classes={classes}
          />
        </MsalProvider>
      )}
    </div>
  )
}

const MicrosoftIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 21 21"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#00a4ef" />
    <rect x="1" y="11" width="9" height="9" fill="#00b04f" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
)

export default Login
